import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Pagination,
  Collapse,
  Tooltip,
  Checkbox,
  Fade,
  Divider,
  Skeleton,
  MenuItem,
} from "@mui/material";
import AppDropdown from "../../components/AppDropdown";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  FileDownload as DownloadIcon,
  FileUpload as UploadIcon,
  Close as CloseIcon,
  PropaneTank,
  PropaneTank as CylinderIcon,
  Tag as TagIcon,
  Straighten as CapacityIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { useColorMode } from "../../contexts/ColorModeContext";
import DeleteConfirmModal from "../../components/common/DeleteConfirmModal";
import "./CylinderMaster.css";
import {
  getCylinders,
  deleteCylinder,
  createCylinder,
  updateCylinder,
  trackCylinder,
} from "../../api/cylinderMasterApi";
import { getAllGoods } from "../../api/goodsApi";
import { BASE_URL } from "../../config/apiconfig";

const CylinderMaster = () => {
  const { mode } = useColorMode();
  const isDark = mode === "dark";

  // Keep Cylinder Master visuals tied to the active MUI color mode.
  // These values intentionally stay local to this page so other pages are unaffected.
  const cm = {
    page: isDark ? "#0F172A" : "#F6F8FB",
    card: isDark ? "#1E293B" : "#FFFFFF",
    surface: isDark ? "#172236" : "#F8FAFC",
    hover: isDark ? "#273449" : "#F1F5F9",
    border: isDark ? "#334155" : "#E2E8F0",
    borderLight: isDark ? "#3B4A61" : "#E5E7EB",
    textPrimary: isDark ? "#F8FAFC" : "#0F172A",
    textSecondary: isDark ? "#CBD5E1" : "#64748B",
  };

  const location = useLocation();
  const [cylinders, setCylinders] = useState([]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    cylinderName: "",
    batchNo: "",
    srno: "",
    manufacturingDate: "",
    testingDate: "",
    cylinderCapacity: "",
  });

  const [searchFilters, setSearchFilters] = useState({
    cylinderName: "",
    srno: "",
    cylinderCapacity: "",
    testingDate: "",
  });

  const hasActiveFilters = searchFilters.cylinderName || searchFilters.srno || searchFilters.cylinderCapacity || searchFilters.testingDate;

  const [goods, setGoods] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);
  const inputRef = useRef(null);

  // Delete confirmation modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [cylinderToDelete, setCylinderToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewCylinder, setViewCylinder] = useState(null);
  const [viewCylinderStatus, setViewCylinderStatus] = useState(null);
  const [viewCylinderStatusLoading, setViewCylinderStatusLoading] = useState(false);
  const [popupMessage, setPopupMessage] = useState({ open: false, title: '', message: '', type: 'info', errorReport: null });
  const [selectedCylinders, setSelectedCylinders] = useState([]);

  const allSelected =
    cylinders.length > 0 && selectedCylinders.length === cylinders.length;
  const someSelected =
    selectedCylinders.length > 0 && selectedCylinders.length < cylinders.length;

  const fetchCylinders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        size,
      };

      Object.entries(searchFilters).forEach(([key, value]) => {
        if (value) {
          params[key] = value;
        }
      });

      const response = await getCylinders(params);
      const data = response.data || response;

      setCylinders(data.content || []);
      setTotalPages(data.totalPages || 0);
      setTotalElements(data.totalElements || 0);
      setPage(data.number || 0);
    } catch (error) {
      console.error("Error fetching cylinders:", error);
      setCylinders([]);
      setPopupMessage({
        open: true,
        title: 'Error Loading Data',
        message: `Failed to load cylinders: ${error.message || 'Unknown error'}`,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [page, size, searchFilters]);

  const fetchGoods = async () => {
    try {
      const response = await getAllGoods();
      const goodsData = response.data?.content || response.data || [];
      setGoods(Array.isArray(goodsData) ? goodsData : []);
    } catch (error) {
      console.error("Error fetching goods:", error);
      setGoods([]);
    }
  };

  useEffect(() => {
    fetchCylinders();
    fetchGoods();
  }, [page, searchFilters, fetchCylinders]);

  useEffect(() => {
    const quickSearch = location.state?.quickSearch;
    if (!quickSearch) return;

    const searchValue = String(quickSearch).trim();
    if (!searchValue) return;

    setSearchFilters({
      cylinderName: searchValue,
      srno: "",
      cylinderCapacity: "",
      testingDate: "",
    });
    setSearchTerm(searchValue);
    setPage(0);
  }, [location.state]);

  // Clear selection whenever the underlying page of data changes
  useEffect(() => {
    setSelectedCylinders([]);
  }, [cylinders]);

  const clearSearchFilters = () => {
    setSearchFilters({
      cylinderName: "",
      srno: "",
      cylinderCapacity: "",
      testingDate: "",
    });
    setSearchTerm("");
    setShowDropdown(false);
    setPage(0);
  };

  const handleSearchChange = (field, value) => {
    setSearchFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    setPage(0);
  };

  const handleDeleteClick = (cylinder) => {
    setCylinderToDelete(cylinder);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!cylinderToDelete) return;

    setIsDeleting(true);
    try {
      if (Array.isArray(cylinderToDelete.id)) {
        // Multiple delete
        await Promise.all(cylinderToDelete.id.map((id) => deleteCylinder(id)));
        setSelectedCylinders([]);
      } else {
        // Single delete
        await deleteCylinder(cylinderToDelete.id);
      }
      setIsDeleteModalOpen(false);
      setCylinderToDelete(null);
      fetchCylinders();
      toast.success("Cylinder(s) deleted successfully");
    } catch (error) {
      console.error("Delete failed:", error);
      if (error.response?.status === 401) {
        toast.error("You don't have permission to delete cylinders");
      } else {
        toast.error("Failed to delete cylinder(s)");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setCylinderToDelete(null);
  };

  const handleOpenModalForEdit = (cylinder) => {
    setFormData({
      cylinderName: cylinder.cylinderName || "",
      batchNo: cylinder.batchNo || "",
      srno: cylinder.srno || "",
      manufacturingDate: cylinder.manufacturingDate || "",
      testingDate: cylinder.testingDate || "",
      cylinderCapacity: cylinder.cylinderCapacity || "",
    });
    setEditingId(cylinder.id);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleOpenModalForCreate = () => {
    setFormData({
      cylinderName: "",
      batchNo: "",
      srno: "",
      manufacturingDate: "",
      testingDate: "",
      cylinderCapacity: "",
    });
    setIsEditing(false);
    setEditingId(null);
    setIsModalOpen(true);
  };

  const handleSaveCylinder = async () => {
    setIsSaving(true);
    try {
      if (isEditing) {
        await updateCylinder(editingId, formData);
        toast.success("Cylinder updated successfully");
      } else {
        await createCylinder(formData);
        toast.success("Cylinder created successfully");
      }

      setIsModalOpen(false);
      fetchCylinders();
    } catch (error) {
      console.error("Save failed:", error);
      if (error.response?.status === 401) {
        toast.error("You don't have permission to create/edit cylinders");
      } else {
        toast.error("Failed to save cylinder");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditing(false);
    setEditingId(null);
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${BASE_URL}/cylinderMaster/export`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Export failed");
      }

      const blob = await response.blob();
      const fileUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = `cylinders-export-${new Date().toISOString().split("T")[0]}.xlsx`;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(fileUrl);
      }, 100);

      setPopupMessage({
        open: true,
        title: 'Export Successful',
        message: 'Cylinders exported successfully',
        type: 'success'
      });
    } catch (error) {
      console.error("Export error:", error);
      setPopupMessage({
        open: true,
        title: 'Export Error',
        message: `Failed to export cylinders: ${error.message || 'Unknown error'}`,
        type: 'error'
      });
    }
  };

  const handleImport = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".xlsx,.xls";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append("file", file);

      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${BASE_URL}/cylinderMaster/import`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          }
        );

        if (!response.ok) {
          throw new Error("Import failed");
        }

        const result = await response.json();
      
      if (result.duplicates > 0) {
        setPopupMessage({
          open: true,
          title: 'Import Completed with Duplicates',
          message: `${result.duplicates} duplicates found, ${result.inserted} new records inserted out of ${result.total} total records`,
          type: 'warning',
          errorReport: result.errorReport
        });
      } else if (result.invalid > 0) {
        setPopupMessage({
          open: true,
          title: 'Import Failed',
          message: `${result.invalid} invalid records out of ${result.total} total records`,
          type: 'error',
          errorReport: result.errorReport
        });
      } else {
        setPopupMessage({
          open: true,
          title: 'Import Successful',
          message: `${result.inserted} records inserted out of ${result.total} total records`,
          type: 'success',
          errorReport: null
        });
      }  
        
        fetchCylinders();
      } catch (error) {
        console.error("Import error:", error);
        setPopupMessage({
          open: true,
          title: 'Import Error',
          message: `Failed to import cylinders: ${error.message || 'Unknown error'}`,
          type: 'error'
        });
      }
    };
    input.click();
  };

  const handleSelectCylinder = (cylinderId) => {
    setSelectedCylinders((prev) =>
      prev.includes(cylinderId)
        ? prev.filter((id) => id !== cylinderId)
        : [...prev, cylinderId]
    );
  };

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedCylinders([]);
    } else {
      setSelectedCylinders(cylinders.map((c) => c.id));
    }
  };

  const handleClearSelection = () => setSelectedCylinders([]);

  const handleDeleteMultiple = () => {
    if (selectedCylinders.length === 0) return;

    setCylinderToDelete({
      id: selectedCylinders,
      cylinderName: `${selectedCylinders.length} cylinders`,
      srno: "multiple",
    });
    setIsDeleteModalOpen(true);
  };

  const handleDownloadErrorReport = () => {
    if (popupMessage.errorReport) {
      const byteCharacters = atob(popupMessage.errorReport);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `duplicate-error-report-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "OUT_WITH_CUSTOMER":
        return "status-badge status-out";
      case "IN_INVENTORY":
        return "status-badge status-in";
      case "RETURNED":
        return "status-badge status-returned";
      default:
        return "status-badge";
    }
  };

  const fetchViewCylinderStatus = useCallback(async (cylinder) => {
    if (!cylinder) return;
    setViewCylinderStatusLoading(true);
    setViewCylinderStatus(null);
    try {
      const response = await trackCylinder(cylinder.cylinderName, cylinder.srno);
      if (response.data) {
        setViewCylinderStatus(response.data);
      }
    } catch (error) {
      console.error("Error fetching cylinder status:", error);
    } finally {
      setViewCylinderStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    if (viewCylinder) {
      fetchViewCylinderStatus(viewCylinder);
    } else {
      setViewCylinderStatus(null);
    }
  }, [viewCylinder, fetchViewCylinderStatus]);

  const handlePageChange = (event, value) => {
    setPage(value - 1);
  };

  const rangeStart = totalElements === 0 ? 0 : page * size + 1;
  const rangeEnd = Math.min((page + 1) * size, totalElements);

  return (
    <Box className="cylinder-master-container" sx={{ backgroundColor: cm.page, color: cm.textPrimary }}>
      {/* Header Section */}
      <Box sx={{ p: "8px 4px 36px", background: 'transparent' }}>
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Delete Cylinder"
        message="Are you sure you want to delete this cylinder? This action cannot be undone."
        itemName={
          cylinderToDelete
            ? `${cylinderToDelete.cylinderName} (SR: ${cylinderToDelete.srno})`
            : ""
        }
        isLoading={isDeleting}
      />

      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: {
            xs: "wrap",
            lg: "nowrap",
          },
          gap: 3,
          mb: 4,
        }}
      >
        {/* Left - Title */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flex: 1,
            minWidth: 280,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 48,
              height: 48,
              background: "#4338ca",
              borderRadius: "12px",
              color: "white",
            }}
          >
            <PropaneTank sx={{ fontSize: 24 }} />
          </Box>
          <Box>
            <Typography
              variant="h4"
              sx={{ fontSize: "2rem", fontWeight: 700, color: "text.primary", mb: 0.5, lineHeight: 1.2 }}
            >
              Cylinder Master
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
              Manage cylinder inventory and tracking
            </Typography>
          </Box>
        </Box>

        {/* Right - Actions */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {/* Export / Import grouped as a single segmented control */}
          <Box
            sx={{
              display: "flex",
              border: "1px solid #E2E8F0",
              borderRadius: "12px",
              overflow: "hidden",
              backgroundColor: "#F8FAFC",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
            }}
          >
            <Button
              onClick={handleExport}
              startIcon={<UploadIcon fontSize="small" sx={{ color: "#2563EB" }} />}
              sx={{
                color: "#2563EB",
                fontWeight: 600,
                borderRadius: 0,
                px: 2,
                py: 1,
                "&:hover": { backgroundColor: "#EFF6FF" },
              }}
            >
              Export
            </Button>
            <Divider 
              orientation="vertical" 
              flexItem 
              sx={{ borderColor: "#E2E8F0" }}
            />
            <Button
              onClick={handleImport}
              startIcon={<DownloadIcon fontSize="small" sx={{ color: "#059669" }} />}
              sx={{
                color: "#059669",
                fontWeight: 600,
                borderRadius: 0,
                px: 2,
                py: 1,
                "&:hover": { backgroundColor: "#ECFDF5" },
              }}
            >
              Import
            </Button>
          </Box>

          {/* Add Cylinder */}
          <Button
            variant="contained"
            disableElevation
            startIcon={<AddIcon />}
            onClick={handleOpenModalForCreate}
            sx={{
              fontWeight: 600,
              boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
            }}
          >
            Add Cylinder
          </Button>
        </Box>
      </Box>

      {/* Filter Toggle Button */}
      {!showFilters ? (
        <Button
          variant="outlined"
          startIcon={<SearchIcon />}
          onClick={() => setShowFilters(true)}
          sx={{
            mb: 3,
            height: 48,
            borderRadius: "10px",
            fontWeight: 600,
            textTransform: "none",
            fontSize: "14px",
            borderColor: "#E5E7EB",
            color: "#64748b",
            "&:hover": {
              borderColor: "#2563eb",
              background: "#eff6ff",
              color: "#2563eb",
            },
          }}
        >
          Show Filters
        </Button>
      ) : null}

      {/* Filters */}
      <Collapse in={showFilters}>
        <Card
          sx={{
            mb: 3,
            borderRadius: "22px",
            border: "1px solid #E5E7EB",
            boxShadow: "0 10px 30px rgba(15,23,42,.05)",
            overflow: "hidden",
            background: "#fff",
            position: "relative",
            zIndex: 1,
          }}
        >
          <CardContent
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              "&:last-child": {
                pb: { xs: 2, sm: 2.5, md: 3 }
              }
            }}
          >
            {/* Header row */}
            <Box sx={{
              display: "flex",
              alignItems: { xs: "center", sm: "flex-start" },
              justifyContent: "space-between",
              mb: { xs: 2, sm: 2.5 },
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: 0 }
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#eff6ff",
                  color: "#2563eb",
                }}>
                  <SearchIcon sx={{ fontSize: 17 }} />
                </Box>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
                  Filters
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
                  onClick={clearSearchFilters}
                  disabled={!hasActiveFilters}
                  sx={{
                    height: 36,
                    mt: { xs: 0, sm: 0.5 },
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "12.5px",
                    textTransform: "none",
                    color: "#64748b",
                    "&:hover": {
                      background: "#f1f5f9",
                      color: "#334155",
                    },
                    "&:disabled": {
                      color: "#cbd5e1",
                      background: "transparent",
                      "&:hover": {
                        background: "transparent",
                        color: "#cbd5e1",
                      }
                    }
                  }}
                >
                  Clear Filters
                </Button>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setShowFilters(false)}
                  sx={{
                    height: 36,
                    mt: { xs: 0, sm: 0.5 },
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "12.5px",
                    textTransform: "none",
                    color: "#64748b",
                    "&:hover": {
                      background: "#f1f5f9",
                      color: "#334155",
                    }
                  }}
                >
                  Close
                </Button>
              </Box>
            </Box>

            {/* Fields row */}
            <Box sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              alignItems: "flex-end",
              gap: { xs: 2, sm: 2, md: 2.5 }
            }}>
              <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: 74 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "#64748B", mb: 0.5 }}>
                  Cylinder Name
                </Typography>
                <Box sx={{
                  position: "relative",
                  width: "100%",
                  height: 46,
                  borderRadius: "12px",
                  backgroundColor: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  transition: "all .25s ease",
                  display: "flex",
                  alignItems: "center",
                  "&:hover": {
                    backgroundColor: "#FFFFFF",
                    borderColor: "#CBD5E1",
                  },
                  "&:focus-within": {
                    backgroundColor: "#FFFFFF",
                    borderColor: "#2563EB",
                    borderWidth: "2px",
                  }
                }}
                ref={inputRef}
                >
                  <Box sx={{ position: "absolute", left: 14, zIndex: 1 }}>
                    <CylinderIcon sx={{ fontSize: 18, color: "#94A3B8" }} />
                  </Box>
                  <input
                    type="text"
                    placeholder="Search by name"
                    value={searchFilters.cylinderName}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      handleSearchChange("cylinderName", e.target.value);
                      setShowDropdown(true);
                    }}
                    onFocus={() => setShowDropdown(true)}
                    style={{
                      width: "100%",
                      height: "100%",
                      padding: "0 14px 0 42px",
                      border: "none",
                      outline: "none",
                      fontSize: "14px",
                      color: "#0F172A",
                      backgroundColor: "transparent",
                      boxSizing: "border-box"
                    }}
                  />
                </Box>
                {showDropdown && inputRef.current && createPortal(
                  <div
                    style={{
                      position: 'fixed',
                      top: `${inputRef.current.getBoundingClientRect().bottom + 4}px`,
                      left: `${inputRef.current.getBoundingClientRect().left}px`,
                      width: `${inputRef.current.offsetWidth}px`,
                      zIndex: 1500,
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <div
                      style={{
                        maxHeight: 220,
                        overflow: "auto",
                        borderRadius: "12px",
                        border: "1px solid #E7EAF3",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                        backgroundColor: "white",
                      }}
                    >
                      {goods
                        .filter((good) => {
                          const cylinderName = good.name || good.cylinderName || "";
                          return cylinderName
                            .toLowerCase()
                            .includes((searchTerm || "").toLowerCase());
                        })
                        .map((good, index) => (
                          <Box
                            key={index}
                            onClick={() => {
                              handleSearchChange("cylinderName", good.name || good.cylinderName);
                              setSearchTerm(good.name || good.cylinderName);
                              setShowDropdown(false);
                            }}
                            sx={{
                              p: 1.25,
                              fontSize: "0.875rem",
                              color: "#0F172A",
                              cursor: "pointer",
                              "&:hover": { backgroundColor: "#F8FAFC" },
                            }}
                          >
                            {good.name || good.cylinderName}
                          </Box>
                        ))}
                    </div>
                  </div>,
                  document.body
                )}
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: 74 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "#64748B", mb: 0.5 }}>
                  Serial Number
                </Typography>
                <Box sx={{
                  position: "relative",
                  width: "100%",
                  height: 46,
                  borderRadius: "12px",
                  backgroundColor: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  transition: "all .25s ease",
                  display: "flex",
                  alignItems: "center",
                  "&:hover": {
                    backgroundColor: "#FFFFFF",
                    borderColor: "#CBD5E1",
                  },
                  "&:focus-within": {
                    backgroundColor: "#FFFFFF",
                    borderColor: "#2563EB",
                    borderWidth: "2px",
                  }
                }}>
                  <Box sx={{ position: "absolute", left: 14, zIndex: 1 }}>
                    <TagIcon sx={{ fontSize: 18, color: "#94A3B8" }} />
                  </Box>
                  <input
                    type="text"
                    placeholder="SR no."
                    value={searchFilters.srno}
                    onChange={(e) => handleSearchChange("srno", e.target.value)}
                    style={{
                      width: "100%",
                      height: "100%",
                      padding: "0 14px 0 42px",
                      border: "none",
                      outline: "none",
                      fontSize: "14px",
                      color: "#0F172A",
                      backgroundColor: "transparent",
                      boxSizing: "border-box"
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: 74 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "#64748B", mb: 0.5 }}>
                  Capacity
                </Typography>
                <Box sx={{
                  position: "relative",
                  width: "100%",
                  height: 46,
                  borderRadius: "12px",
                  backgroundColor: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  transition: "all .25s ease",
                  display: "flex",
                  alignItems: "center",
                  "&:hover": {
                    backgroundColor: "#FFFFFF",
                    borderColor: "#CBD5E1",
                  },
                  "&:focus-within": {
                    backgroundColor: "#FFFFFF",
                    borderColor: "#2563EB",
                    borderWidth: "2px",
                  }
                }}>
                  <Box sx={{ position: "absolute", left: 14, zIndex: 1 }}>
                    <CapacityIcon sx={{ fontSize: 18, color: "#94A3B8" }} />
                  </Box>
                  <input
                    type="text"
                    placeholder="e.g. 14.2 kg"
                    value={searchFilters.cylinderCapacity}
                    onChange={(e) => handleSearchChange("cylinderCapacity", e.target.value)}
                    style={{
                      width: "100%",
                      height: "100%",
                      padding: "0 14px 0 42px",
                      border: "none",
                      outline: "none",
                      fontSize: "14px",
                      color: "#0F172A",
                      backgroundColor: "transparent",
                      boxSizing: "border-box"
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: 74 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "#64748B", mb: 0.5 }}>
                  Testing Date
                </Typography>
                <Box sx={{
                  position: "relative",
                  width: "100%",
                  height: 46,
                  borderRadius: "12px",
                  backgroundColor: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  transition: "all .25s ease",
                  display: "flex",
                  alignItems: "center",
                  "&:hover": {
                    backgroundColor: "#FFFFFF",
                    borderColor: "#CBD5E1",
                  },
                  "&:focus-within": {
                    backgroundColor: "#FFFFFF",
                    borderColor: "#2563EB",
                    borderWidth: "2px",
                  }
                }}>
                  <input
                    type="date"
                    value={searchFilters.testingDate}
                    onChange={(e) => handleSearchChange("testingDate", e.target.value)}
                    style={{
                      width: "100%",
                      height: "100%",
                      padding: "0 14px",
                      border: "none",
                      outline: "none",
                      fontSize: "14px",
                      color: "#0F172A",
                      backgroundColor: "transparent",
                      boxSizing: "border-box"
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Collapse>

      {/* Table */}
      <Card className="cylinder-master-table-card" sx={{ borderRadius: '8px', boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.30)' : '0 2px 4px rgba(0,0,0,0.1)', overflow: "visible", backgroundColor: cm.card, color: cm.textPrimary, border: `1px solid ${cm.border}` }}>
        {/* Contextual selection bar — only appears once rows are checked */}
        <Fade in={selectedCylinders.length > 0} unmountOnExit>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              py: 1.5,
              background: "linear-gradient(135deg, #1E293B 0%, #0F172A 100%)",
              color: "#F8FAFC",
              borderBottom: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <IconButton
                size="small"
                onClick={handleClearSelection}
                aria-label="Clear selection"
                sx={{
                  color: "#CBD5E1",
                  backgroundColor: "rgba(255,255,255,0.06)",
                  width: 28,
                  height: 28,
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.12)",
                    color: "#fff",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 24,
                  height: 24,
                  px: 1,
                  borderRadius: "999px",
                  backgroundColor: "rgba(99,102,241,0.25)",
                  border: "1px solid rgba(129,140,248,0.4)",
                }}
              >
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#C7D2FE" }}>
                  {selectedCylinders.length}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#E2E8F0" }}>
                selected
              </Typography>
            </Box>
            <Button
              size="small"
              disableElevation
              startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
              onClick={handleDeleteMultiple}
              sx={{
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.8125rem",
                textTransform: "none",
                px: 2,
                py: 0.75,
                borderRadius: "10px",
                backgroundColor: "#DC2626",
                boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                "&:hover": {
                  backgroundColor: "#B91C1C",
                  boxShadow: "0 4px 10px rgba(220,38,38,0.35)",
                },
              }}
            >
              Delete
            </Button>
          </Box>
        </Fade>

        <CardContent sx={{ p: 0, backgroundColor: cm.card, color: cm.textPrimary }}>
            <TableContainer
              sx={{
                maxHeight: 640,
                overflowY: 'auto',
                maxWidth: '100%',
                overflowX: 'auto',
                "&::-webkit-scrollbar": {
                  width: 8,
                  height: 8,
                },
                "&::-webkit-scrollbar-thumb": {
                  borderRadius: 20,
                  background: "#cbd5e1",
                },
                backgroundColor: cm.card,
              }}
            >
              <Table stickyHeader className="cylinder-master-table" sx={{
                tableLayout: "auto",
                width: "100%",
                backgroundColor: cm.card,
                color: cm.textPrimary,
              }}>
                <TableHead sx={{
                  '& th': { position: 'sticky', top: 0, zIndex: 5, backgroundColor: cm.surface, color: cm.textPrimary }
                }}>
                  <TableRow>
                    <TableCell
                      sx={{
                        width: { xs: 36, sm: 42 },
                        minWidth: { xs: 36, sm: 42 },
                        maxWidth: { xs: 36, sm: 42 },
                        p: 0,
                        textAlign: "center",
                        verticalAlign: "middle",
                        backgroundColor: cm.surface,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: { xs: 48, sm: 54 },
                          width: "100%",
                        }}
                      >
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected}
                          onChange={handleSelectAll}
                          size="small"
                          sx={{
                            p: 0,
                            m: 0,
                            "& svg": {
                              fontSize: { xs: 18, sm: 20 },
                            },
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        width: { xs: 140, sm: 180, md: 200 },
                        minWidth: { xs: 140, sm: 180, md: 200 },
                        fontWeight: 700,
                        backgroundColor: cm.surface,
                        fontSize: { xs: 12, sm: 13 },
                        padding: { xs: '8px 8px', sm: '10px 10px', md: '12px' },
                        whiteSpace: 'nowrap',
                        pl: { xs: 0.5, sm: 1 },
                        pr: 1,
                      }}
                    >
                      CYLINDER NAME
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        width: { xs: 80, sm: 90, md: 100 },
                        minWidth: { xs: 80, sm: 90, md: 100 },
                        fontWeight: 700,
                        backgroundColor: cm.surface,
                        fontSize: { xs: 12, sm: 13 },
                        padding: { xs: '8px 8px', sm: '10px 10px', md: '12px' },
                        whiteSpace: 'nowrap'
                      }}
                    >
                      BATCH NO
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        width: { xs: 70, sm: 80, md: 90 },
                        minWidth: { xs: 70, sm: 80, md: 90 },
                        fontWeight: 700,
                        backgroundColor: cm.surface,
                        fontSize: { xs: 12, sm: 13 },
                        padding: { xs: '8px 8px', sm: '10px 10px', md: '12px' },
                        whiteSpace: 'nowrap'
                      }}
                    >
                      SR NO
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        width: { xs: 100, sm: 110, md: 130 },
                        minWidth: { xs: 100, sm: 110, md: 130 },
                        fontWeight: 700,
                        backgroundColor: cm.surface,
                        fontSize: { xs: 12, sm: 13 },
                        padding: { xs: '8px 8px', sm: '10px 10px', md: '12px' },
                        whiteSpace: 'nowrap'
                      }}
                    >
                      MFG DATE
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        width: { xs: 100, sm: 110, md: 130 },
                        minWidth: { xs: 100, sm: 110, md: 130 },
                        fontWeight: 700,
                        backgroundColor: cm.surface,
                        fontSize: { xs: 12, sm: 13 },
                        padding: { xs: '8px 8px', sm: '10px 10px', md: '12px' },
                        whiteSpace: 'nowrap'
                      }}
                    >
                      TESTING DATE
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        width: { xs: 60, sm: 70, md: 80 },
                        minWidth: { xs: 60, sm: 70, md: 80 },
                        fontWeight: 700,
                        backgroundColor: cm.surface,
                        fontSize: { xs: 12, sm: 13 },
                        padding: { xs: '8px 8px', sm: '10px 10px', md: '12px' },
                        whiteSpace: 'nowrap'
                      }}
                    >
                      CAPACITY
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        width: { xs: 100, sm: 110, md: 130 },
                        minWidth: { xs: 100, sm: 110, md: 130 },
                        fontWeight: 700,
                        backgroundColor: cm.surface,
                        fontSize: { xs: 12, sm: 13 },
                        padding: { xs: '8px 8px', sm: '10px 10px', md: '12px' },
                        whiteSpace: 'nowrap'
                      }}
                    >
                      STATUS
                    </TableCell>
                    <TableCell align="center" sx={{
                      width: { xs: 80, sm: 90, md: 100 },
                      minWidth: { xs: 80, sm: 90, md: 100 },
                      fontWeight: 700,
                      backgroundColor: cm.surface,
                      fontSize: { xs: 12, sm: 13 },
                      padding: { xs: '8px 6px', sm: '10px 8px', md: '12px 8px' },
                    }}>
                      <Box sx={{ width: 118, maxWidth: '100%', mx: 'auto', textAlign: 'center' }}>
                        ACTIONS
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
              <TableBody>
                {loading ? (
                  [...Array(10)].map((_, index) => (
                    <TableRow key={index} sx={{ height: 64 }}>
                      <TableCell padding="checkbox"><Skeleton variant="rectangular" width={20} height={20} /></TableCell>
                      <TableCell><Skeleton variant="text" width={120} /></TableCell>
                      <TableCell align="right"><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell align="right"><Skeleton variant="text" width={100} /></TableCell>
                      <TableCell align="right"><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell align="right"><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell align="right"><Skeleton variant="text" width={60} /></TableCell>
                      <TableCell align="center"><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Skeleton variant="circular" width={34} height={34} />
                          <Skeleton variant="circular" width={34} height={34} />
                          <Skeleton variant="circular" width={34} height={34} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : cylinders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ fontSize: 48, opacity: 0.5 }}>🔧</Box>
                        <Typography variant="h6" color="text.primary" fontWeight={600}>
                          No Cylinders Found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Try changing your filters or add a new cylinder
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  cylinders.map((cylinder, idx) => {
                    const isSelected = selectedCylinders.includes(cylinder.id);
                    return (
                      <TableRow
                        key={cylinder.id}
                        hover
                        selected={isSelected}
                        sx={{
                          height: { xs: 48, sm: 54 },
                          "& td": {
                            py: 0,
                            px: { xs: 1, sm: 1.25 },
                            verticalAlign: "middle",
                            borderBottom: "1px solid #EEF2F7",
                          },
                          "&:hover": {
                            backgroundColor: cm.surface,
                            transition: "all .2s ease"
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            width: { xs: 36, sm: 42 },
                            minWidth: { xs: 36, sm: 42 },
                            maxWidth: { xs: 36, sm: 42 },
                            p: 0,
                            verticalAlign: "middle",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              height: { xs: 48, sm: 54 },
                              width: "100%",
                            }}
                          >
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleSelectCylinder(cylinder.id)}
                              size="small"
                              sx={{
                                p: 0,
                                m: 0,
                                "& svg": {
                                  fontSize: { xs: 18, sm: 20 },
                                },
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            width: { xs: 140, sm: 180, md: 200 },
                            minWidth: { xs: 140, sm: 180, md: 200 },
                            pl: { xs: 0.5, sm: 1 },
                            pr: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              m: 0,
                              ml: 0,
                              p: 0,
                              fontSize: { xs: 12, sm: 13 },
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {cylinder.cylinderName || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ width: { xs: 80, sm: 90, md: 100 }, minWidth: { xs: 80, sm: 90, md: 100 }, color: "text.secondary", whiteSpace: "nowrap", fontSize: { xs: 12, sm: 13 } }}>
                          {cylinder.batchNo || "—"}
                        </TableCell>
                        <TableCell align="center" sx={{ width: { xs: 70, sm: 80, md: 90 }, minWidth: { xs: 70, sm: 80, md: 90 }, color: "text.secondary", whiteSpace: "nowrap", fontSize: { xs: 12, sm: 13 } }}>
                          {cylinder.srno || "—"}
                        </TableCell>
                        <TableCell align="center" sx={{ width: { xs: 100, sm: 110, md: 130 }, minWidth: { xs: 100, sm: 110, md: 130 }, color: "text.secondary", whiteSpace: "nowrap", fontSize: { xs: 12, sm: 13 } }}>
                          {cylinder.manufacturingDate || "—"}
                        </TableCell>
                        <TableCell align="center" sx={{ width: { xs: 100, sm: 110, md: 130 }, minWidth: { xs: 100, sm: 110, md: 130 }, color: "text.secondary", whiteSpace: "nowrap", fontSize: { xs: 12, sm: 13 } }}>
                          {cylinder.testingDate || "—"}
                        </TableCell>
                        <TableCell align="center" sx={{ width: { xs: 60, sm: 70, md: 80 }, minWidth: { xs: 60, sm: 70, md: 80 }, color: "text.secondary", whiteSpace: "nowrap", fontSize: { xs: 12, sm: 13 } }}>
                          {cylinder.cylinderCapacity || "—"}
                        </TableCell>
                        <TableCell align="center" sx={{ width: { xs: 100, sm: 110, md: 130 }, minWidth: { xs: 100, sm: 110, md: 130 }, color: "text.secondary", whiteSpace: "nowrap", fontSize: { xs: 12, sm: 13 } }}>
                          {cylinder.currentStatus ? (
                            <span className={getStatusBadgeClass(cylinder.currentStatus)}>
                              {cylinder.currentStatus.replace(/_/g, " ")}
                            </span>
                          ) : "—"}
                        </TableCell>
                        <TableCell align="center" sx={{ width: { xs: 80, sm: 90, md: 100 }, minWidth: { xs: 80, sm: 90, md: 100 }, padding: { xs: '8px 6px', sm: '10px 8px', md: '12px 8px' } }}>
                          <Box sx={{
                            width: 100,
                            maxWidth: '100%',
                            mx: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: .25,
                            flexWrap: 'nowrap',
                          }}>
                            <Tooltip title="View">
                              <IconButton
                                onClick={() => setViewCylinder(cylinder)}
                                sx={{
                                  color: '#2563EB',
                                  width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 }, borderRadius: '8px',
                                  '&:hover': { background: cm.hover, transform: 'translateY(-2px)' }
                                }}
                              >
                                <ViewIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                onClick={() => handleOpenModalForEdit(cylinder)}
                                sx={{
                                  color: '#F59E0B',
                                  width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 }, borderRadius: '8px',
                                  '&:hover': { background: cm.hover, transform: 'translateY(-2px)' }
                                }}
                              >
                                <EditIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                onClick={() => handleDeleteClick(cylinder)}
                                sx={{
                                  color: '#EF4444',
                                  width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 }, borderRadius: '8px',
                                  '&:hover': { background: cm.hover, transform: 'translateY(-2px)' }
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>

        {/* Unified footer: record count · rows-per-page · pagination */}
        {!loading && totalElements > 0 && (
          <Box
            className="cylinder-master-footer"
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              px: 3,
              py: 1.5,
              borderTop: "1px solid",
              borderColor: cm.border,
              overflow: "visible",
              backgroundColor: cm.card,
              color: cm.textPrimary,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: { xs: "center", md: "flex-start" }, gap: 1 }}>
              {totalElements > 0 && (
                  <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                    Showing {rangeStart}–{rangeEnd} of {totalElements} records
                  </Typography>
                )}
                <Pagination
                count={totalPages}
                page={page + 1}
                onChange={handlePageChange}
                disabled={loading}
                shape="rounded"
                variant="outlined"
                color="primary"
                showFirstButton
                showLastButton
                siblingCount={0}
                boundaryCount={1}
                size="small"
                sx={{
                  "& .MuiPaginationItem-root": {
                    transition: "transform .2s, box-shadow .2s, background-color .2s",
                    color: cm.textSecondary,
                    borderColor: cm.border,
                    backgroundColor: cm.surface,
                    borderRadius: "10px",
                    fontWeight: 600,
                    minWidth: 38,
                    height: 38,
                  },
                  "& .MuiPaginationItem-root:hover": {
                    backgroundColor: cm.hover,
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 10px rgba(0,0,0,.15)",
                  },
                  "& .MuiPaginationItem-root:active": {
                    transform: "scale(.95)",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#2563EB !important",
                    color: "#FFFFFF !important",
                    borderColor: "#2563EB !important",
                    transform: "scale(1.05)",
                    boxShadow: "0 6px 14px rgba(37,99,235,.25)",
                  },
                  "& ul": {
                    justifyContent: {
                      xs: "center",
                      md: "flex-start",
                    },
                  },
                }}
              />
            </Box>
            <Box
              sx={{
                width: {
                  xs: 120,
                  sm: 130,
                  md: 140,
                },
                transition: "all .2s",
                position: "relative",
                zIndex: 1000,
                backgroundColor: cm.card,
              }}
            >
              <Typography sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.875rem" }, fontWeight: 500, color: cm.textSecondary, mb: 0.75 }}>Rows per page</Typography>
              <Box sx={{ position: "relative", zIndex: 1000 }}>
                <AppDropdown
                  value={size}
                  options={[
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                    { value: 20, label: '20' },
                    { value: 50, label: '50' },
                    { value: 100, label: '100' }
                  ]}
                  onChange={(newSize) => {
                    setSize(newSize);
                    setPage(0);
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Card>
      </Box>

      {/* Add/Edit Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            overflow: "hidden",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          }
        }}
      >
        <Box sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          display: "flex",
          alignItems: "center",
          gap: { xs: 1.5, sm: 2 },
        }}>
          <Box sx={{
            width: { xs: 40, sm: 48 },
            height: { xs: 40, sm: 48 },
            borderRadius: "12px",
            backgroundColor: "#EEF2FF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}>
            <CylinderIcon sx={{ fontSize: { xs: 24, sm: 28 }, color: "#4F46E5" }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <DialogTitle sx={{ 
              fontSize: { xs: "1.25rem", sm: "1.5rem" }, 
              fontWeight: 700, 
              color: "#0F172A",
              p: 0,
              mb: 0.5,
              lineHeight: 1.2,
            }}>
              {isEditing ? "Edit Cylinder" : "Add New Cylinder"}
            </DialogTitle>
            <Typography sx={{ 
              fontSize: { xs: "0.8rem", sm: "0.875rem" }, 
              color: "#64748B",
              fontWeight: 400,
            }}>
              {isEditing ? "Update cylinder details below" : "Fill in the cylinder details below"}
            </Typography>
          </Box>
        </Box>
        <DialogContent sx={{ 
          pt: { xs: 2, sm: 3 }, 
          pb: { xs: 1.5, sm: 2 }, 
          px: { xs: 2, sm: 3 },
          overflowY: "auto",
          flex: 1,
        }}>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: "100%", gap: 0.5 }}>
                <Typography sx={{ 
                  fontSize: { xs: "13px", sm: "14px" }, 
                  fontWeight: 500, 
                  color: "#64748B",
                  ml: 1,
                }}>
                  Cylinder Name
                </Typography>
                <TextField
                  fullWidth
                  select
                  value={formData.cylinderName}
                  onChange={(e) => setFormData({ ...formData, cylinderName: e.target.value })}
                  slotProps={{
                    select: {
                      displayEmpty: true,
                      MenuProps: {
                        slotProps: {
                          paper: {
                            sx: {
                              minWidth: 250,
                              borderRadius: 2,
                            },
                          },
                        },
                      },
                    },
                  }}
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      width: "100%",
                      height: { xs: 42, sm: 48 },
                      borderRadius: "12px",
                      background: "#F8FAFC",
                      "& fieldset": {
                        borderColor: "#E2E8F0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#CBD5E1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#4F46E5",
                        borderWidth: 2,
                      },
                    },
                    "& .MuiSelect-select": {
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      boxSizing: "border-box",
                      padding: { xs: "10px 42px 10px 14px !important", sm: "12px 42px 12px 14px !important" },
                      fontSize: { xs: "13px", sm: "14px" },
                    },
                  }}
                >
                  <MenuItem value="">Select cylinder name</MenuItem>
                  {goods.map((good) => (
                    <MenuItem key={good.id} value={good.name}>
                      {good.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: "100%", gap: 0.5 }}>
                <Typography sx={{ 
                  fontSize: { xs: "13px", sm: "14px" }, 
                  fontWeight: 500, 
                  color: "#64748B",
                  ml: 1,
                }}>
                  Batch No
                </Typography>
                <TextField
                  fullWidth
                  value={formData.batchNo}
                  onChange={(e) => setFormData({ ...formData, batchNo: e.target.value })}
                  placeholder="Enter batch number"
                  variant="outlined"
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      width: "100%",
                      height: { xs: 42, sm: 48 },
                      borderRadius: "12px",
                      background: "#F8FAFC",
                      "& fieldset": {
                        borderColor: "#E2E8F0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#CBD5E1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#4F46E5",
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputBase-input": {
                      height: { xs: 42, sm: 48 },
                      padding: { xs: "10px 14px", sm: "12px 14px" },
                      fontSize: { xs: "13px", sm: "14px" },
                      boxSizing: "border-box",
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: "100%", gap: 0.5 }}>
                <Typography sx={{ 
                  fontSize: { xs: "13px", sm: "14px" }, 
                  fontWeight: 500, 
                  color: "#64748B",
                  ml: 1,
                }}>
                  SR No
                </Typography>
                <TextField
                  fullWidth
                  value={formData.srno}
                  onChange={(e) => setFormData({ ...formData, srno: e.target.value })}
                  placeholder="Enter serial number"
                  variant="outlined"
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      width: "100%",
                      height: { xs: 42, sm: 48 },
                      borderRadius: "12px",
                      background: "#F8FAFC",
                      "& fieldset": {
                        borderColor: "#E2E8F0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#CBD5E1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#4F46E5",
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputBase-input": {
                      height: { xs: 42, sm: 48 },
                      padding: { xs: "10px 14px", sm: "12px 14px" },
                      fontSize: { xs: "13px", sm: "14px" },
                      boxSizing: "border-box",
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: "100%", gap: 0.5 }}>
                <Typography sx={{ 
                  fontSize: { xs: "13px", sm: "14px" }, 
                  fontWeight: 500, 
                  color: "#64748B",
                  ml: 1,
                }}>
                  Capacity
                </Typography>
                <TextField
                  fullWidth
                  value={formData.cylinderCapacity}
                  onChange={(e) => setFormData({ ...formData, cylinderCapacity: e.target.value })}
                  placeholder="Enter cylinder capacity"
                  variant="outlined"
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      width: "100%",
                      height: { xs: 42, sm: 48 },
                      borderRadius: "12px",
                      background: "#F8FAFC",
                      "& fieldset": {
                        borderColor: "#E2E8F0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#CBD5E1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#4F46E5",
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputBase-input": {
                      height: { xs: 42, sm: 48 },
                      padding: { xs: "10px 14px", sm: "12px 14px" },
                      fontSize: { xs: "13px", sm: "14px" },
                      boxSizing: "border-box",
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: "100%", gap: 0.5 }}>
                <Typography sx={{ 
                  fontSize: { xs: "13px", sm: "14px" }, 
                  fontWeight: 500, 
                  color: "#64748B",
                  ml: 1,
                }}>
                  Manufacturing Date
                </Typography>
                <TextField
                  fullWidth
                  type="date"
                  value={formData.manufacturingDate}
                  onChange={(e) => setFormData({ ...formData, manufacturingDate: e.target.value })}
                  variant="outlined"
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      width: "100%",
                      height: { xs: 42, sm: 48 },
                      borderRadius: "12px",
                      background: "#F8FAFC",
                      "& fieldset": {
                        borderColor: "#E2E8F0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#CBD5E1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#4F46E5",
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputBase-input": {
                      height: { xs: 42, sm: 48 },
                      padding: { xs: "10px 14px", sm: "12px 14px" },
                      fontSize: { xs: "13px", sm: "14px" },
                      boxSizing: "border-box",
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: "100%", gap: 0.5 }}>
                <Typography sx={{ 
                  fontSize: { xs: "13px", sm: "14px" }, 
                  fontWeight: 500, 
                  color: "#64748B",
                  ml: 1,
                }}>
                  Testing Date
                </Typography>
                <TextField
                  fullWidth
                  type="date"
                  value={formData.testingDate}
                  onChange={(e) => setFormData({ ...formData, testingDate: e.target.value })}
                  variant="outlined"
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      width: "100%",
                      height: { xs: 42, sm: 48 },
                      borderRadius: "12px",
                      background: "#F8FAFC",
                      "& fieldset": {
                        borderColor: "#E2E8F0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#CBD5E1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#4F46E5",
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputBase-input": {
                      height: { xs: 42, sm: 48 },
                      padding: { xs: "10px 14px", sm: "12px 14px" },
                      fontSize: { xs: "13px", sm: "14px" },
                      boxSizing: "border-box",
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          p: { xs: 2, sm: 3 }, 
          gap: 2,
          flexShrink: 0,
        }}>
          <Button 
            onClick={handleCloseModal}
            disabled={isSaving}
            sx={{
              height: { xs: 44, sm: 48 },
              borderRadius: "12px",
              fontWeight: 600,
              textTransform: "none",
              fontSize: { xs: "14px", sm: "15px" },
              px: { xs: 2, sm: 3 },
              borderColor: "#E2E8F0",
              color: "#64748B",
              backgroundColor: "white",
              "&:hover": {
                borderColor: "#CBD5E1",
                backgroundColor: "#F1F5F9",
                color: "#0F172A",
              },
              "&:disabled": {
                opacity: 0.6,
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveCylinder} 
            variant="contained"
            disabled={isSaving}
            sx={{
              height: { xs: 44, sm: 48 },
              borderRadius: "12px",
              fontWeight: 600,
              textTransform: "none",
              fontSize: { xs: "14px", sm: "15px" },
              px: { xs: 2, sm: 3 },
              background: "#4F46E5",
              "&:hover": {
                background: "#4338CA",
                boxShadow: "0 4px 12px rgba(79, 70, 229, 0.4)",
              },
              "&:disabled": {
                background: "#94A3B8",
                boxShadow: "none",
              }
            }}
          >
            {isSaving ? "Saving..." : (isEditing ? "Update Cylinder" : "Save Cylinder")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Modal */}
      <Dialog 
        open={!!viewCylinder} 
        onClose={() => setViewCylinder(null)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            overflow: "hidden",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          }
        }}
      >
        <Box sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          display: "flex",
          alignItems: "center",
          gap: { xs: 1.5, sm: 2 },
        }}>
          <Box sx={{
            width: { xs: 40, sm: 48 },
            height: { xs: 40, sm: 48 },
            borderRadius: "12px",
            backgroundColor: "#EEF2FF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}>
            <PropaneTank sx={{ fontSize: { xs: 24, sm: 28 }, color: "#4F46E5" }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <DialogTitle sx={{ 
              fontSize: { xs: "1.25rem", sm: "1.5rem" }, 
              fontWeight: 700, 
              color: "#0F172A",
              p: 0,
              mb: 0.5,
              lineHeight: 1.2,
            }}>
              Cylinder Details
            </DialogTitle>
            <Typography sx={{ 
              fontSize: { xs: "0.8rem", sm: "0.875rem" }, 
              color: "#64748B",
              fontWeight: 400,
            }}>
              View cylinder information
            </Typography>
          </Box>
        </Box>
        <DialogContent sx={{ 
          pt: { xs: 2, sm: 3 }, 
          pb: { xs: 1.5, sm: 2 }, 
          px: { xs: 2, sm: 3 },
          overflowY: "auto",
          flex: 1,
        }}>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: "100%", gap: 0.5 }}>
                <Typography sx={{ 
                  fontSize: { xs: "13px", sm: "14px" }, 
                  fontWeight: 500, 
                  color: "#64748B",
                  ml: 1,
                }}>
                  Cylinder Name
                </Typography>
                <Box sx={{
                  width: "100%",
                  height: { xs: 42, sm: 48 },
                  borderRadius: "12px",
                  background: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  display: "flex",
                  alignItems: "center",
                  px: { xs: 2, sm: 2.5 },
                }}>
                  <Typography sx={{ 
                    fontSize: { xs: "13px", sm: "14px" }, 
                    fontWeight: 600, 
                    color: "#0F172A",
                  }}>
                    {viewCylinder?.cylinderName || "—"}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: "100%", gap: 0.5 }}>
                <Typography sx={{ 
                  fontSize: { xs: "13px", sm: "14px" }, 
                  fontWeight: 500, 
                  color: "#64748B",
                  ml: 1,
                }}>
                  Batch No
                </Typography>
                <Box sx={{
                  width: "100%",
                  height: { xs: 42, sm: 48 },
                  borderRadius: "12px",
                  background: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  display: "flex",
                  alignItems: "center",
                  px: { xs: 2, sm: 2.5 },
                }}>
                  <Typography sx={{ 
                    fontSize: { xs: "13px", sm: "14px" }, 
                    fontWeight: 600, 
                    color: "#0F172A",
                  }}>
                    {viewCylinder?.batchNo || "—"}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: "100%", gap: 0.5 }}>
                <Typography sx={{ 
                  fontSize: { xs: "13px", sm: "14px" }, 
                  fontWeight: 500, 
                  color: "#64748B",
                  ml: 1,
                }}>
                  SR No
                </Typography>
                <Box sx={{
                  width: "100%",
                  height: { xs: 42, sm: 48 },
                  borderRadius: "12px",
                  background: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  display: "flex",
                  alignItems: "center",
                  px: { xs: 2, sm: 2.5 },
                }}>
                  <Typography sx={{ 
                    fontSize: { xs: "13px", sm: "14px" }, 
                    fontWeight: 600, 
                    color: "#0F172A",
                  }}>
                    {viewCylinder?.srno || "—"}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: "100%", gap: 0.5 }}>
                <Typography sx={{ 
                  fontSize: { xs: "13px", sm: "14px" }, 
                  fontWeight: 500, 
                  color: "#64748B",
                  ml: 1,
                }}>
                  Capacity
                </Typography>
                <Box sx={{
                  width: "100%",
                  height: { xs: 42, sm: 48 },
                  borderRadius: "12px",
                  background: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  display: "flex",
                  alignItems: "center",
                  px: { xs: 2, sm: 2.5 },
                }}>
                  <Typography sx={{ 
                    fontSize: { xs: "13px", sm: "14px" }, 
                    fontWeight: 600, 
                    color: "#0F172A",
                  }}>
                    {viewCylinder?.cylinderCapacity || "—"}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: "100%", gap: 0.5 }}>
                <Typography sx={{ 
                  fontSize: { xs: "13px", sm: "14px" }, 
                  fontWeight: 500, 
                  color: "#64748B",
                  ml: 1,
                }}>
                  Current Status
                </Typography>
                <Box sx={{
                  width: "100%",
                  height: { xs: 42, sm: 48 },
                  borderRadius: "12px",
                  background: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  display: "flex",
                  alignItems: "center",
                  px: { xs: 2, sm: 2.5 },
                }}>
                  {viewCylinderStatusLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid #2563EB', borderTopColor: 'transparent', animation: 'spin 1s linear infinite' }} />
                      <Typography sx={{ fontSize: { xs: "13px", sm: "14px" }, color: "#64748B" }}>Loading...</Typography>
                    </Box>
                  ) : viewCylinderStatus?.currentStatus ? (
                    <span className={getStatusBadgeClass(viewCylinderStatus.currentStatus)}>
                      {viewCylinderStatus.currentStatus.replace(/_/g, " ")}
                    </span>
                  ) : (
                    <Typography sx={{ fontSize: { xs: "13px", sm: "14px" }, color: "#64748B" }}>—</Typography>
                  )}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: "100%", gap: 0.5 }}>
                <Typography sx={{ 
                  fontSize: { xs: "13px", sm: "14px" }, 
                  fontWeight: 500, 
                  color: "#64748B",
                  ml: 1,
                }}>
                  Manufacturing Date
                </Typography>
                <Box sx={{
                  width: "100%",
                  height: { xs: 42, sm: 48 },
                  borderRadius: "12px",
                  background: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  display: "flex",
                  alignItems: "center",
                  px: { xs: 2, sm: 2.5 },
                }}>
                  <Typography sx={{ 
                    fontSize: { xs: "13px", sm: "14px" }, 
                    fontWeight: 600, 
                    color: "#0F172A",
                  }}>
                    {viewCylinder?.manufacturingDate || "—"}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: "100%", gap: 0.5 }}>
                <Typography sx={{ 
                  fontSize: { xs: "13px", sm: "14px" }, 
                  fontWeight: 500, 
                  color: "#64748B",
                  ml: 1,
                }}>
                  Testing Date
                </Typography>
                <Box sx={{
                  width: "100%",
                  height: { xs: 42, sm: 48 },
                  borderRadius: "12px",
                  background: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  display: "flex",
                  alignItems: "center",
                  px: { xs: 2, sm: 2.5 },
                }}>
                  <Typography sx={{ 
                    fontSize: { xs: "13px", sm: "14px" }, 
                    fontWeight: 600, 
                    color: "#0F172A",
                  }}>
                    {viewCylinder?.testingDate || "—"}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          p: { xs: 2, sm: 3 }, 
          gap: 2,
          flexShrink: 0,
        }}>
          <Button 
            onClick={() => setViewCylinder(null)}
            sx={{
              height: { xs: 44, sm: 48 },
              borderRadius: "12px",
              fontWeight: 600,
              textTransform: "none",
              fontSize: { xs: "14px", sm: "15px" },
              px: { xs: 2, sm: 3 },
              borderColor: "#E2E8F0",
              color: "#64748B",
              backgroundColor: "white",
              "&:hover": {
                borderColor: "#CBD5E1",
                backgroundColor: "#F1F5F9",
                color: "#0F172A",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Popup Message Dialog */}
      <Dialog
        open={popupMessage.open}
        onClose={() => setPopupMessage({ ...popupMessage, open: false })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          }
        }}
      >
        <DialogTitle sx={{
          fontSize: "1.25rem",
          fontWeight: 700,
          color: popupMessage.type === 'error' ? '#EF4444' : popupMessage.type === 'warning' ? '#F59E0B' : '#10B981',
          pb: 1
        }}>
          {popupMessage.title}
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography variant="body1" sx={{ color: '#64748B', lineHeight: 1.6 }}>
            {popupMessage.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
          {popupMessage.errorReport && (
            <Button
              onClick={handleDownloadErrorReport}
              variant="outlined"
              startIcon={<DownloadIcon />}
              sx={{
                borderRadius: "10px",
                fontWeight: 600,
                textTransform: "none",
                borderColor: '#E2E8F0',
                color: '#64748B',
                '&:hover': {
                  borderColor: '#CBD5E1',
                  backgroundColor: '#F8FAFC',
                }
              }}
            >
              Download Report
            </Button>
          )}
          <Button
            onClick={() => setPopupMessage({ ...popupMessage, open: false })}
            variant="contained"
            sx={{
              borderRadius: "10px",
              fontWeight: 600,
              textTransform: "none",
              backgroundColor: popupMessage.type === 'error' ? '#EF4444' : popupMessage.type === 'warning' ? '#F59E0B' : '#10B981',
              '&:hover': {
                backgroundColor: popupMessage.type === 'error' ? '#DC2626' : popupMessage.type === 'warning' ? '#D97706' : '#059669',
              }
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CylinderMaster;